import {
  createContext,
  useCallback,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { CloseOutlined, ExportOutlined, FilePdfOutlined } from "@ant-design/icons";
import { Button, Modal, Tooltip } from "antd";
import {
  isManagedBlobUrl,
  revokeCurrentBlobUrl,
} from "@/shared/lib/document-view";
import { PdfObject } from "./pdf-object";
import "./viewer-provider.scss";

type ViewerFile = {
  url: string;
  fileName: string;
  fileId?: string;
};

type ViewerContextValue = {
  setPDF: (file: ViewerFile) => void;
};

export const ViewerContext = createContext<ViewerContextValue>({
  setPDF: () => undefined,
});

export const ViewerProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [previewFile, setPreviewFile] = useState<ViewerFile | null>(null);

  const setPDF = useCallback((file: ViewerFile) => {
    if (!file.url) {
      return;
    }

    setPreviewFile(file);
  }, []);

  const handleClose = useCallback(() => {
    if (previewFile?.url && isManagedBlobUrl(previewFile.url)) {
      revokeCurrentBlobUrl();
    }

    setPreviewFile(null);
  }, [previewFile?.url]);

  const handleOpenInNewTab = useCallback(() => {
    if (!previewFile?.url) {
      return;
    }

    window.open(previewFile.url, "_blank", "noopener,noreferrer");
  }, [previewFile?.url]);

  const value = useMemo(() => ({ setPDF }), [setPDF]);

  return (
    <ViewerContext.Provider value={value}>
      {children}
      <Modal
        open={Boolean(previewFile)}
        onCancel={handleClose}
        footer={null}
        closable={false}
        centered
        width="min(1200px, 92vw)"
        className="bd-pdf-viewer"
        destroyOnClose
        maskClosable
        keyboard
        zIndex={2100}
      >
        <header className="bd-pdf-viewer__header">
          <div className="bd-pdf-viewer__title-wrap">
            <FilePdfOutlined className="bd-pdf-viewer__title-icon" />
            <div>
              <div className="bd-pdf-viewer__title-label">Просмотр документа</div>
              <div className="bd-pdf-viewer__title">
                {previewFile?.fileName ?? "Без имени"}
              </div>
            </div>
          </div>
          <div className="bd-pdf-viewer__actions">
            <Tooltip title="Открыть в новой вкладке">
              <Button
                type="default"
                icon={<ExportOutlined />}
                onClick={handleOpenInNewTab}
              >
                Новая вкладка
              </Button>
            </Tooltip>
            <Tooltip title="Закрыть">
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleClose}
                aria-label="Закрыть"
              />
            </Tooltip>
          </div>
        </header>
        <div className="bd-pdf-viewer__body">
          {previewFile ? (
            <PdfObject
              url={previewFile.url}
              fileName={previewFile.fileName}
            />
          ) : null}
        </div>
      </Modal>
    </ViewerContext.Provider>
  );
};
